import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import ReactQuill from "react-quill";
import api from "../../api";
import Logo from "../../images/logo-2.png";
import add from "../../images/add.png";
import "react-quill/dist/quill.snow.css";
import { useSelector } from "react-redux";

// Lucide Icons
import {
  FileText,
  X,
  Save,
  Type,
  Folder,
  FileEdit,
  Hash,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import "./AddTemplateModal.css";
import { useAlert } from "react-alert";

export function AddTemplateModal({ getTemplate, setShow, allCategory }) {
  const adminStore = useSelector((state) => state.adminReducer);
  const alert = useAlert();

  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState({});
  const [characterCount, setCharacterCount] = useState(0);

  const [inputData, setInputData] = useState({
    description: "",
    title: "",
    category: "",
  });

  const validateForm = () => {
    const newErrors = {};

    if (!inputData.title.trim()) {
      newErrors.title = "Template title is required";
    } else if (inputData.title.length < 3) {
      newErrors.title = "Title must be at least 3 characters";
    }

    if (!inputData.category) {
      newErrors.category = "Please select a category";
    }

    if (
      !inputData.description.trim() ||
      inputData.description === "<p><br></p>"
    ) {
      newErrors.description = "Template content is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (inputData?.description && inputData?.title && inputData.category) {
        setLoader(true);
        let payload = {
          _id: adminStore.admin.admin._id,
          title: inputData.title,
          template: inputData.description,
          category: inputData.category || "",
        };
        const signalResponse = await api.addTemplate(payload);
        if (signalResponse?.success) {
          alert.success("Template added successfully");
          setInputData({
            description: "",
            title: "",
            category: "",
          });
          setLoader(false);
          getTemplate();
          setShow(false);
        } else {
          setLoader(false);
          alert.error("Something went wrong");
        }
      } else {
        setLoader(false);
        alert.error("Please insert all fields");
      }
    } catch (error) {
      setLoader(false);
      console.log(error);
      alert.error("Something went wrong");
    }
  };

  var Editor = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image", "video"],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const handleChange = (content) => {
    setInputData({ ...inputData, description: content });

    // Calculate character count (excluding HTML tags)
    const textContent = content.replace(/<[^>]*>/g, "");
    setCharacterCount(textContent.length);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleSubmit();
    }
  };

  return (
    <>
      <div className="add-template-container">
        {/* Header */}
        <div className="template-header">
          <div className="header-icon">
            <FileText size={28} className="icon-primary" />
          </div>
          <div className="modal-header-content">
            <h2 className="template-title">Create New Template</h2>
            <p className="template-subtitle">
              Add a new template to your library
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="template-form">
          {/* Title Input */}
          <div className="form-section">
            <label htmlFor="templateTitle" className="form-label">
              <Type size={16} />
              Template Title
            </label>
            <div className="input-wrapper">
              <div className="input-icon">
                <Hash size={18} />
              </div>
              <input
                type="text"
                id="templateTitle"
                value={inputData?.title}
                onChange={(e) =>
                  setInputData({
                    ...inputData,
                    title: e.target.value,
                  })
                }
                onKeyPress={handleKeyPress}
                className={`form-input ${errors.title ? "error" : ""}`}
                placeholder="e.g., Welcome Email, Invoice Template, etc."
                autoFocus
              />
              {inputData.title && (
                <button
                  className="clear-input"
                  onClick={() => setInputData({ ...inputData, title: "" })}
                >
                  <X size={16} />
                </button>
              )}
            </div>
            {errors.title && (
              <div className="error-message">
                <AlertCircle size={12} />
                <span>{errors.title}</span>
              </div>
            )}
            {inputData.title && (
              <div className="character-counter">
                <div className="counter">{inputData.title.length}/100</div>
                <div className="counter-label">characters</div>
              </div>
            )}
          </div>

          {/* Category Selection */}
          <div className="form-section">
            <label htmlFor="templateCategory" className="form-label">
              <Folder size={16} />
              Category
            </label>
            <div className="select-wrapper">
              <div className="select-icon">
                <Folder size={18} />
              </div>
              <select
                id="templateCategory"
                onChange={(e) =>
                  setInputData({
                    ...inputData,
                    category: e.target.value,
                  })
                }
                value={inputData?.category}
                className={`form-select ${errors.category ? "error" : ""}`}
              >
                <option value="">Select a category</option>
                {allCategory.map((val, key) => (
                  <option key={key} value={val?._id}>
                    {val?.name}
                  </option>
                ))}
              </select>
              <div className="select-arrow">
                <i className="fa-solid fa-caret-down" />
              </div>
            </div>
            {errors.category && (
              <div className="error-message">
                <AlertCircle size={12} />
                <span>{errors.category}</span>
              </div>
            )}
          </div>

          {/* Rich Text Editor */}
          <div className="form-section">
            <label className="form-label">
              <FileEdit size={16} />
              Template Content
            </label>
            <div
              className={`editor-wrapper ${errors.description ? "error" : ""}`}
            >
              <ReactQuill
                placeholder="Start typing your template here..."
                modules={Editor}
                theme="snow"
                value={inputData.description}
                onChange={handleChange}
                className="rich-text-editor"
              />
            </div>
            {errors.description && (
              <div className="error-message">
                <AlertCircle size={12} />
                <span>{errors.description}</span>
              </div>
            )}

            {/* Editor Stats */}
            {/* <div className="editor-stats">
              <div className="stats-item">
                <span className="stat-label">Characters:</span>
                <span className={`stat-value ${characterCount > 5000 ? 'warning' : ''}`}>
                  {characterCount}
                </span>
              </div>
              <div className="stats-item">
                <span className="stat-label">Lines:</span>
                <span className="stat-value">
                  {inputData.description.split('<p>').length - 1}
                </span>
              </div>
            </div> */}

            {/* Formatting Tips */}
            {/* <div className="formatting-tips">
              <div className="tips-label">Formatting Tips:</div>
              <ul className="tips-list">
                <li>Use headers for section titles</li>
                <li>Add lists for step-by-step instructions</li>
                <li>Include placeholders like [Company Name] or [User Name]</li>
                <li>Use bold/italic for emphasis</li>
              </ul>
            </div> */}
          </div>

          {/* Action Buttons */}
          <div className="modal-action-buttons">
            <div className="button-group">
              <button
                className="cancel-btn"
                onClick={() => setShow(false)}
                disabled={loader}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="submit-btn"
                disabled={loader}
              >
                {loader ? (
                  <div className="btn-loader"></div>
                ) : (
                  <>
                    <Save size={18} />
                    <span>Create Template</span>
                  </>
                )}
              </button>
            </div>
            {/* <div className="shortcut-hint">
              <CheckCircle size={14} />
              <span>Press Ctrl+Enter to submit</span>
            </div> */}
          </div>
        </div>
      </div>

      {loader && (
        <div className="seekoLoader">
          <div className="loader">
            <img src={Logo} alt="loader" />
          </div>
        </div>
      )}
    </>
  );
}
